function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

// eslint-disable-next-line no-unused-vars
class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

// eslint-disable-next-line no-unused-vars
class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container, preventScroll = false) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus({
    preventScroll,
  });

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  // eslint-disable-next-line no-unused-vars
  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true,
  );
}

// eslint-disable-next-line no-unused-vars
function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('input', this.forceDigits.bind(this));
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this)),
    );
    this.input.addEventListener('keydown', this.onInputEnterPress.bind(this));
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    // eslint-disable-next-line no-undef
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  forceDigits(event) {
    this.input.value = event.target.value.replace(/\D/g, '');
    event.target.value = this.input.value;
  }

  // eslint-disable-next-line no-unused-vars
  onInputChange(event) {
    this.validateQtyRules();
    // eslint-disable-next-line no-undef
    publish(PUB_SUB_EVENTS.addQuantityUpdate, { data: { value: this.input.value } });
  }

  onInputEnterPress(event) {
    if (this.dataset.context === 'cart' && event.key === 'Enter') {
      event.preventDefault();
      this.input.dispatchEvent(this.changeEvent);
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;
    // eslint-disable-next-line no-unused-vars
    const page = this.dataset.context === 'cart' ? 'cart-page' : 'product-detail';
    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
    // eslint-disable-next-line no-undef
    publish(PUB_SUB_EVENTS.addQuantityUpdate, { data: { value: this.input.value } });
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

// eslint-disable-next-line no-unused-vars
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

// eslint-disable-next-line no-unused-vars
function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

// eslint-disable-next-line no-unused-vars
function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  // eslint-disable-next-line no-unused-vars
  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        // eslint-disable-next-line no-redeclare
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    // eslint-disable-next-line no-unused-vars
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this)),
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)',
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
      summaryElement.setAttribute('data-link-text', 'headerMenuClose');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    setTimeout(() => {
      elementToFocus.setAttribute('data-link-text', 'headerMenuOpen');
    });
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener, { preventScroll = false } = {}) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'), preventScroll);
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
    // eslint-disable-next-line no-undef
    publish(PUB_SUB_EVENTS.modalDialogClose, { modal: this.id });
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalDialogLoyalty extends HTMLElement {
  constructor() {
    super();
    this.persistDismissal = this.dataset.persistDismissal === 'true';
    this.modalId = this.id || `modal-${Date.now()}`;
    const closeButton = this.querySelector('[id^="ModalClose-"]');
    closeButton?.addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');

    // Check if modal should remain hidden based on previous dismissal
    if (this.persistDismissal && this.shouldStayHidden()) {
      this.preventAutoShow = true;
    } else {
      this.setAttribute('open', '');
    }
  }

  shouldStayHidden() {
    if (!window.ModalStorage) {
      return false;
    }
    return window.ModalStorage.isModalDismissed(this.modalId);
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();

    // Store dismissal state if persistence is enabled
    if (this.persistDismissal) {
      this.storeDismissal();
    }
    // eslint-disable-next-line no-undef
    publish(PUB_SUB_EVENTS.modalDialogClose, { modal: this.id });
  }

  storeDismissal() {
    if (!window.ModalStorage) {
      this.setAttribute('open', '');
      return;
    }
    const success = window.ModalStorage.setModalDismissed(this.modalId, this.dataset.persistDismissalDuration || 24);
    if (success) {
      console.log(`[Modal] "${this.modalId}" dismissal saved - will hide for 24 hours`);
    }
  }

  clearDismissal() {
    if (!window.ModalStorage) {
      return;
    }
    window.ModalStorage.clearModalDismissal(this.modalId);
    this.preventAutoShow = false;
  }
}
customElements.define('modal-dialog-loyalty', ModalDialogLoyalty);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`),
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    this.init();
  }

  init() {
    if (this.initialized) {
      return;
    }

    const button = this.querySelector('button');
    if (!button) {
      return;
    }

    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });

    this.initialized = true;
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) {
      if (this.dataset.isAplusPdp === 'true') {
        this.initUspCardClickTracking();
        this.initUspCardHoverTracking();
      }
      return;
    }

    this.initPages();
    // eslint-disable-next-line no-unused-vars
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton?.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));

    this.slider.addEventListener('mousedown', this.recordMouseState);
    this.slider.addEventListener('dragstart', this.recordMouseState);
    this.slider.addEventListener('dragend', this.recordMouseState);
    document.addEventListener('mouseup', this.recordMouseState);
    this.slider.addEventListener('mousemove', this.onClickAndDrag);

    if (this.dataset.isAplusPdp === 'true') {
      this.initUspCardClickTracking();
      this.initUspCardHoverTracking();
    }
  }

  initUspCardClickTracking() {
    this.addEventListener('click', (event) => {
      const anchor = event.target.closest('.multicolumn-card__info a[data-link-text="uspCardClick"]');
      if (!anchor) return;

      const targetURL = anchor.href || '';

      // Prevent immediate navigation so the analytics event is not cancelled
      event.preventDefault();

      window.Shopify.analytics.publish('page-click', {
        clickData: {
          pageName: document.title,
          pageType: 'product',
          moduleName: 'pdp:aplus:usps',
          locURL: window.location.pathname,
          linkTitle: 'uspCardClick',
          linkText: 'uspCardClick',
          targetURL,
          filters: null,
          searchTerm: null,
        },
      });

      // Navigate after a short delay to allow the analytics beacon to dispatch
      if (targetURL) {
        setTimeout(() => {
          window.location.href = targetURL;
        }, 0);
      }
    });
  }

  initUspCardHoverTracking() {
    // Desktop only — skip on touch devices
    if (window.matchMedia('(hover: none)').matches) return;

    let hoverTimer = null;
    this.addEventListener('mouseover', (event) => {
      const anchor = event.target.closest('.multicolumn-card__info a[data-link-text="uspCardClick"]');
      if (!anchor) return;

      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(() => {
        window.Shopify.analytics.publish('page-click', {
          clickData: {
            pageName: document.title,
            pageType: 'product',
            moduleName: 'pdp:aplus:usps',
            locURL: window.location.pathname,
            linkTitle: 'uspCardHover',
            linkText: 'uspCardHover',
            targetURL: '',
            filters: null,
            searchTerm: null,
          },
        });
      }, 1000);
    });

    this.addEventListener('mouseout', (event) => {
      const card = event.target.closest('.multicolumn-card');
      if (card) clearTimeout(hoverTimer);
    });
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.max(
      1,
      Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset),
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    // A peeking track stops scrollLeft short of the final page, so the count reads
    // one low on the last slide. Opt-in, since other sliders rely on the raw value.
    if (
      this.dataset.exactCounter === 'true' &&
      this.sliderItemsToShow.length > 0 &&
      this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])
    ) {
      this.currentPage = this.totalPages;
    }

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        }),
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton?.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton?.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || this.slidesPerPage;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  recordMouseState = (e) => {
    this.mouseX = e.screenX;
    this.mousedown = ['mousedown', 'dragstart'].includes(e.type);
  };

  onClickAndDrag = (e) => {
    if (!this.mousedown) {
      return;
    }
    const direction = this.mouseX > e.screenX ? 1 : -1;
    const step = (direction > 0 ? this.nextButton : this.prevButton).dataset.step || this.slidesPerPage;
    this.slideScrollPosition = this.slider.scrollLeft + direction * step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
    this.mouseX = e.screenX;
  };

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;
    this.isAplusPdp = this.dataset.isAplusPdp === 'true';
    this.slideshowId = this.dataset.slideshowId || 'lifestyle';

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.isAplusPdp) {
      this.initSwipeTracking();
      if (this.prevButton) {
        this.prevButton.dataset.linkText = `${this.slideshowId}PrevSlide`;
        this.prevButton.dataset.analyticsLinkTitle = `${this.slideshowId}PrevSlide`;
      }
      if (this.nextButton) {
        this.nextButton.dataset.linkText = `${this.slideshowId}NextSlide`;
        this.nextButton.dataset.analyticsLinkTitle = `${this.slideshowId}NextSlide`;
      }
    }

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button?.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true },
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  sendSlideshowAnalytics(linkText) {
    if (!this.isAplusPdp) return;
    window.Shopify.analytics.publish('page-click', {
      clickData: {
        pageName: document.title,
        pageType: 'product',
        moduleName: `pdp:aplus:${this.slideshowId}`,
        locURL: window.location.pathname,
        linkTitle: linkText,
        linkText: linkText,
        targetURL: '',
        filters: null,
        searchTerm: null,
      },
    });
  }

  initSwipeTracking() {
    let touchStartX = 0;
    this.slider.addEventListener(
      'touchstart',
      (e) => {
        touchStartX = e.touches[0].clientX;
      },
      { passive: true },
    );
    this.slider.addEventListener(
      'touchend',
      (e) => {
        const touchEndX = e.changedTouches[0].clientX;
        const diff = touchStartX - touchEndX;
        if (Math.abs(diff) > 50) {
          this.sendSlideshowAnalytics(`${this.slideshowId}Swipe`);
        }
      },
      { passive: true },
    );
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;
    const isNextButton = event.currentTarget.name === 'next';
    const isPreviousButton = event.currentTarget.name === 'previous';

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }
    if (isLastSlide && isNextButton) {
      this.slideScrollPosition = 0;
    } else if (isFirstSlide && isPreviousButton) {
      this.slideScrollPosition = this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isNextButton) {
      this.slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth;
    } else {
      this.slideScrollPosition = this.slider.scrollLeft - this.sliderFirstItemNode.clientWidth;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton?.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  // eslint-disable-next-line no-unused-vars
  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const direction = 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();

    const targetIndex = this.sliderControlLinksArray.indexOf(event.currentTarget);
    if (targetIndex === -1) return;

    const slideScrollPosition = this.sliderFirstItemNode.clientWidth * targetIndex;

    this.wasClicked = true;

    this.setSlidePosition(slideScrollPosition);

    // Update current page immediately
    this.currentPage = targetIndex + 1;

    // Update active dot state
    this.update();

    // Update visibility/accessibility
    this.setSlideVisibility();

    // Apply announcement animation if enabled
    const previousPage = this.currentPage;

    this.currentPage = targetIndex + 1;
    this.applyAnimationToAnnouncementBar(targetIndex + 1 > previousPage ? 'next' : 'previous');
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      // eslint-disable-next-line no-undef
      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }
  sendClickAnalytics(legend, value) {
    const isAplusPdp = this.dataset.isAplusPdp === 'true';
    window.Shopify.analytics.publish('page-click', {
      clickData: {
        pageName: 'product-details',
        moduleName: isAplusPdp ? 'pdp:aplus' : 'product-details-variant-selects',
        locURL: window.location.pathname, // Current page URL path
        linkTitle: `Select ${legend} - ${value}`,
        linkText: isAplusPdp ? 'variantSelect' : 'icon:radio',
        targetURL: '',
      },
    });
  }
  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;
    let legend = null;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        ?.removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');
      legend = target.parentElement.parentElement.querySelector('label')?.textContent?.trim() ?? '';
      if (!window?.colorSwatchEnabled) {
        this.sendClickAnalytics(legend, value);
      }
      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        ?.querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset',
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
      // Find the closest fieldset and then its legend
      const fieldset = target.closest('fieldset');
      legend = fieldset ? fieldset.querySelector('legend')?.textContent : null;
      if (!window?.colorSwatchEnabled) {
        this.sendClickAnalytics(legend, value);
      }
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId,
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' },
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

// eslint-disable-next-line no-unused-vars
class CartPerformance {
  static #metric_prefix = 'cart-performance';

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    // eslint-disable-next-line no-unused-vars
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp,
    });

    // eslint-disable-next-line no-unused-vars
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(benchmarkName, `${metricName}:start`, `${metricName}:end`);
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    // eslint-disable-next-line no-unused-vars
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(benchmarkName, startMarker.name, `${metricName}:end`);
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    // eslint-disable-next-line no-unused-vars
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    // eslint-disable-next-line no-unused-vars
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(benchmarkName, `${metricName}:start`, `${metricName}:end`);
  }
}

const elementsArr = document.querySelectorAll(
  '.rich-text__text h1, .image-with-text__text p, .rich-text__text ul li, .rich-text__text h3, .rich-text__text p, .newsletter__subheading p, h2.rich-text__heading, .multicolumn-card__info h3, .multicolumn-card__info p, h2.collapsible-content__heading, h2.image-with-text__heading, h2.newsletter__heading, .rich-text__text h4, .multicolumn-card h1, h2.inline-richtext, h1.collection-hero__title, .filters-panel h2, .filters-panel h3, .banner__heading, .banner__text',
);
elementsArr.forEach((ele) => {
  ele.setAttribute('tabindex', 0);
});

(function () {
  'use strict';
  let analyticsTimeout = null;
  document.addEventListener('DOMContentLoaded', function () {
    const analyticsModules = document.querySelectorAll('analytics-module[data-module-name="menuGrid"]');

    analyticsModules.forEach(function (module) {
      const moduleName = module.getAttribute('data-module-name');
      const gridItems = module.querySelectorAll('.mobile-menu-grid-list__item');

      gridItems.forEach(function (item) {
        const cardInfo = item.querySelector('.mobile-menu-grid-card__info');

        if (cardInfo) {
          const h3Element = cardInfo.querySelector('h3');
          const linkElement = item.querySelector('a.full-unstyled-link, a.link');

          if (h3Element) {
            const itemName = h3Element.textContent.trim();
            const targetURL = linkElement ? linkElement.getAttribute('href') : '';

            item.addEventListener('click', function (event) {
              handleGridItemClick(event, item, itemName, targetURL, moduleName);
            });
          }
        }
      });
    });
  });

  function handleGridItemClick(event, item, itemName, targetURL, moduleName) {
    if (item.dataset.processing === 'true') return;
    item.dataset.processing = 'true';

    const clickedElement = event.target;
    let clickType = 'title';

    if (
      clickedElement.closest('.mobile-menu-grid-card__image-wrapper') ||
      clickedElement.classList.contains('mobile-menu-grid-card__image') ||
      clickedElement.tagName === 'IMG'
    ) {
      clickType = 'image';
    } else if (
      clickedElement.closest('.mobile-menu-grid-card__info') ||
      clickedElement.tagName === 'H3' ||
      clickedElement.classList.contains('rte')
    ) {
      clickType = 'title';
    }

    const trackingData = {
      interactionName: itemName,
      targetURL: targetURL || '',
      moduleName: moduleName + ':' + clickType,
      pageType: document.body.dataset.pageType || '',
    };

    sendAnalyticsEvent(trackingData);

    setTimeout(() => {
      item.dataset.processing = 'false';
    }, 400);
  }

  function sendAnalyticsEvent(data) {
    if (analyticsTimeout) {
      clearTimeout(analyticsTimeout);
    }

    analyticsTimeout = setTimeout(() => {
      const clickData = {
        pageName: window.location.pathname,
        pageType: data.pageType,
        moduleName: data.moduleName,
        locURL: window.location.href,
        linkTitle: data.interactionName,
        targetURL: data.targetURL || '',
        filters: null,
        searchTerm: null,
      };
      sessionStorage.setItem('click-data', JSON.stringify(clickData));
      analyticsTimeout = null;
    }, 50);
  }
})();

(function () {
  'use strict';
  let analyticsTimeout = null;
  document.addEventListener('DOMContentLoaded', function () {
    const analyticsModules = document.querySelectorAll('analytics-module[data-module-name="main:Rich Text"]');

    analyticsModules.forEach(function (module) {
      const moduleName = 'main:richText';
      const headings = module.querySelectorAll('.rich-text__text.rte h2');

      headings.forEach(function (heading) {
        const linkElements = heading.querySelectorAll('a');

        linkElements.forEach(function (link) {
          const linkText = link.textContent.trim();
          const targetURL = link.getAttribute('href') || '';

          link.addEventListener('click', function (event) {
            handleHeadingLinkClick(event, link, linkText, targetURL, moduleName);
          });
        });
      });
    });
  });

  function handleHeadingLinkClick(event, link, linkText, targetURL, moduleName) {
    if (link.dataset.processing === 'true') return;
    link.dataset.processing = 'true';

    const trackingData = {
      interactionName: linkText,
      targetURL: targetURL || '',
      moduleName: moduleName + '::link',
      pageType: document.body.dataset.pageType || '',
    };

    sendAnalyticsEvent(trackingData);

    setTimeout(() => {
      link.dataset.processing = 'false';
    }, 400);
  }

  function sendAnalyticsEvent(data) {
    if (analyticsTimeout) {
      clearTimeout(analyticsTimeout);
    }

    analyticsTimeout = setTimeout(() => {
      const clickData = {
        pageName: window.location.pathname,
        pageType: data.pageType,
        moduleName: data.moduleName,
        locURL: window.location.href,
        linkTitle: data.interactionName,
        targetURL: data.targetURL || '',
        filters: null,
        searchTerm: null,
      };

      sessionStorage.setItem('click-data', JSON.stringify(clickData));
      analyticsTimeout = null;
    }, 50);
  }
})();

// Added analytics for wishlist icon click in header.
// The event is published on 'page-click' with linkTitle and linkText as 'wishlistIconClick' and moduleName as 'navigation:main'.
document.addEventListener('DOMContentLoaded', function () {
  const wishlistIcon = document.querySelector('.header__icons a[aria-label="Wishlist"]');

  if (!wishlistIcon) return;

  wishlistIcon.addEventListener('click', function () {
    window.ShopifyAnalytics = window.ShopifyAnalytics || {};

    window.Shopify.analytics.publish('page-click', {
      clickData: {
        pageName: '{{ page_title | escape }}',
        pageType: '{{ template.name | escape }}',
        moduleName: 'navigation:main',
        locURL: window.location.pathname,
        linkTitle: 'wishlistIconClick',
        linkText: 'wishlistIconClick',
        targetURL: wishlistIcon.href || null,
        filters: null,
        searchTerm: null,
      },
    });
  });
});

// Cart Update Event
document.addEventListener('DOMContentLoaded', () => {
  window.ShopifyAnalytics = window.ShopifyAnalytics || {};

  const cartEndpoints = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];

  function cartUpdate(cart) {
    const cartData = {
      cartId: cart.token,
      lineItems: cart.items.map((item) => ({
        variantId: item.variant_id,
        price: {
          amount: (item.price / 100).toFixed(2),
          currency: Shopify.currency.active,
        },
        quantity: item.quantity,
      })),
    };

    window.Shopify.analytics.publish('cart_updated', {
      cartData,
    });
  }

  function fetchAndUpdateCart() {
    fetch('/cart.js')
      .then((res) => res.json())
      .then((cart) => cartUpdate(cart));
  }

  // ─── Algolia Insights: add-to-cart conversion ────────────────────────
  //
  // B1 fix: the dedupe used to live only here, as a local Map, which meant
  // algolia-pdp-tracking.js's independent cart-diff path (same /cart/add,
  // but reached via comparing /cart.js before/after) had no way to see it and
  // could still double-report. The dedupe now lives in
  // AlgoliaInsights.publishConversion itself, keyed on indexName|objectID|
  // queryID, so every caller — this one, algolia-pdp-tracking.js, or any
  // future one — shares the exact same 2s window instead of each keeping
  // its own copy that can drift or miss the other's recent send.
  function trackAlgoliaConversion(payload) {
    if (!window.AlgoliaInsights) return;

    const items = payload && payload.items ? payload.items : [payload];
    items.forEach((item) => {
      if (!item || !item.product_id) return;

      // The products index is variant-level, so a cart line's product_id is not
      // the Algolia objectID. resolveAttribution matches on either and hands
      // back the objectID that was actually indexed — report that, not this.
      // Matching on product_id also keeps attribution when the shopper adds a
      // different variant than the one search surfaced.
      const attribution = window.AlgoliaInsights.resolveAttribution(item.product_id);
      if (!attribution || !attribution.objectID) return; // never fabricate attribution

      // Surface follows the originating click: a conversion reached via an
      // autocomplete click belongs to the autocomplete segment, not the grid.
      // publishConversion is the single shared dedupe gate (see comment above).
      window.AlgoliaInsights.publishConversion(
        attribution.surface || 'instantsearch',
        attribution.indexName,
        attribution.objectID,
        attribution.queryID,
      );
    });
  }

  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;

    if (url && cartEndpoints.some((endpoint) => url.includes(endpoint))) {
      fetchAndUpdateCart();

      // clone() first — reading the body here would leave the caller's own
      // handler with a consumed stream and break the cart drawer.
      if (url.includes('/cart/add') && response.ok) {
        response
          .clone()
          .json()
          .then(trackAlgoliaConversion)
          .catch(() => {});
      }
    }

    return response;
  };

  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this._url = url;
    return originalOpen.apply(this, [method, url, ...rest]);
  };

  XMLHttpRequest.prototype.send = function (...args) {
    this.addEventListener('load', function () {
      if (this._url && cartEndpoints.some((endpoint) => this._url.includes(endpoint))) {
        fetchAndUpdateCart();
      }
    });

    return originalSend.apply(this, args);
  };
});

// Form Submitted Event for Email Signup Widget
document.addEventListener('DOMContentLoaded', function () {
  const urlParams = new URLSearchParams(window.location.search);

  // Check if this is a successful form submission (after page reload)
  if (urlParams.get('customer_posted') === 'true') {
    // Try multiple selectors to find the email input
    const emailInput =
      document.querySelector('#contact_form input[type="email"]') ||
      document.querySelector('[id^="NewsletterForm-"][type="email"]') ||
      document.querySelector('input[name*="email"]');

    // Try to get email from URL hash parameters or session storage
    let emailValue = null;

    // Check if email is stored in session storage (set during form submit)
    const storedEmail = sessionStorage.getItem('submittedEmail');
    if (storedEmail) {
      emailValue = storedEmail;
      sessionStorage.removeItem('submittedEmail'); // Clean up
    }

    // Fallback to current input value (might be empty after reload)
    if (!emailValue && emailInput) {
      emailValue = emailInput.value;
    }

    if (emailValue && window.Shopify?.analytics?.publish) {
      window.Shopify.analytics.publish('mag_form_submitted', {
        source: 'email-signup-widget',
        formId: 'email-signup-banner',
        formName: 'Email Signup Banner',
        emailAddress: emailValue,
        phoneNumber: null,
      });
    }
  }

  // Add submit listener to capture email before page reload
  const contactForm = document.querySelector('#contact_form');
  if (contactForm) {
    contactForm.addEventListener('submit', function () {
      const emailInput = contactForm.querySelector('input[type="email"]');
      if (emailInput && emailInput.value) {
        // Store email in session storage before page reload
        sessionStorage.setItem('submittedEmail', emailInput.value);
      }
    });
  }
});

// Form Submitted Event for shopify-forms
(function () {
  const processedEmbeds = new WeakSet();
  let debounceTimer = null;

  function attachListener(embed) {
    if (!embed.shadowRoot) return;
    if (processedEmbeds.has(embed)) return;
    processedEmbeds.add(embed);
    const shadow = embed.shadowRoot;
    shadow.addEventListener('click', function (e) {
      const submitBtn = shadow.querySelector('[data-testid="btn-form-submit"]');
      if (!submitBtn) return;
      if (e.target === submitBtn) {
        const email = shadow.querySelector('#email')?.value || null;
        const phone = shadow.querySelector('#phone_number')?.value || null;

        window.Shopify.analytics.publish('mag_form_submitted', {
          source: 'shopify-optin-modal',
          formId: embed.id.replace('app-embed-container-', ''),
          formName: 'Shopify Opt-In Modal',
          emailAddress: email,
          phoneNumber: phone,
        });
      }
    });
  }

  function scanForEmbeds() {
    //console.count('scanForEmbeds'); 
    document.querySelectorAll('shopify-forms-embed[id^="app-embed-container-"]').forEach(attachListener);
  }

  // Initial scan
  scanForEmbeds();

  // Debounced scan function - only scans when nodes are actually added
  function debouncedScan(mutationsList) {
    // Filter: only scan if nodes were actually added
    const hasAddedNodes = mutationsList.some(
      (mutation) => mutation.type === 'childList' && mutation.addedNodes.length > 0
    );

    if (!hasAddedNodes) return;

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      scanForEmbeds();
    }, 100);
  }

  // Observe future embeds with optimized settings
  const observer = new MutationObserver(debouncedScan);

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
})();
